import { useCallback, useRef, useState } from "react";
import {
    buildFilterChips,
    everythingVisibleSelectedFromInstance,
    getGrownSetColumns,
    omitFilterColumns,
} from "./appliedFilterChipsUtils";

/**
 * Keeps applied-filter chips in sync with an AG Grid instance.
 * Both the column header menus and the sidebar Filter panel update the same filter model,
 * so wiring `onFilterChanged` to the grid covers either entry point.
 * @param {Object<string, Set<string>>} staticOptionSets Fixed option lists keyed by field.
 * @returns {{ chips: Array, onFilterChanged: Function, removeFilter: Function, savedFilterModelRef: object }}
 */
const useAppliedFilterChips = (staticOptionSets) => {
    const [chips, setChips] = useState([]);
    const savedFilterModelRef = useRef(null);

    const onFilterChanged = useCallback((params) => {
        const api = params?.api;
        if (!api?.getFilterModel) return;

        const filterModel = api.getFilterModel();
        // savedFilterModelRef.current (the previously applied model) lets buildFilterChips tell whether
        // the user just GREW a column's selection to cover every shown option; only then is it dropped
        // as "select all". It also lets the grid restore the model after a remount (handleGridReady).
        const prevModel = savedFilterModelRef.current;

        // Build the chips (with the select-all verdict applied) and prune any "select all" column so AG
        // Grid clears its blue header icon. The pruned model is idempotent (those columns are simply
        // absent on the re-fired filterChanged), so there is no loop.
        const apply = (isSelectAllColumn) => {
            const { chips: next, selectAllColIds, filterModel: fm } =
                buildFilterChips(api, params.context, staticOptionSets, prevModel, isSelectAllColumn);

            setChips(next);

            if (selectAllColIds.length && api.setFilterModel) {
                const pruned = omitFilterColumns(fm, selectAllColIds);
                savedFilterModelRef.current = Object.keys(pruned).length ? pruned : null;
                api.setFilterModel(pruned);
                return;
            }

            savedFilterModelRef.current = Object.keys(fm).length ? fm : null;
        };

        // For the columns the user just grew, ask AG Grid whether every option its popup shows is now
        // ticked (its own "(Select All)" signal). This matches the popup exactly — the narrowed/full value
        // lists do not — so a column clears precisely when AG Grid would check "(Select All)". The lookup
        // is async (getColumnFilterInstance), so resolve it first, then build. When it is unavailable
        // (older api/tests) buildFilterChips falls back to the full value set.
        const grown = getGrownSetColumns(filterModel, prevModel);
        if (grown.length && typeof api.getColumnFilterInstance === "function") {
            Promise.all(
                grown.map((colId) =>
                    Promise.resolve(api.getColumnFilterInstance(colId))
                        .then((instance) => [colId, everythingVisibleSelectedFromInstance(instance)])
                        .catch(() => [colId, null])
                )
            ).then((entries) => {
                const verdicts = Object.fromEntries(entries);
                apply((colId) => (colId in verdicts ? verdicts[colId] : null));
            });
            return;
        }

        apply(null);
    }, [staticOptionSets]);

    // Clearing one column re-runs grid filtering; the resulting event rebuilds the chips.
    const removeFilter = useCallback((api, colId) => {
        if (!api?.getFilterModel) return;
        api.setFilterModel(omitFilterColumns(api.getFilterModel(), [colId]));
    }, []);

    return { chips, onFilterChanged, removeFilter, savedFilterModelRef };
};

export default useAppliedFilterChips;
